import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";
import { CATALOG_TABS, panelId, tabId, type CatalogTab } from "./tab-ids";

interface CatalogTabsProps {
  readonly value: CatalogTab;
  readonly onChange: (next: CatalogTab) => void;
}

/**
 * Roving tabindex + arrow / Home / End keyboard navigation as described in
 * the WAI-ARIA APG Tabs pattern. Selection follows focus, which matches the
 * spec for "automatic activation" tabs (the only mode we have today).
 *
 * https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
export function CatalogTabs({ value, onChange }: CatalogTabsProps) {
  const { t } = useTranslation();
  const tabRefs = useRef<Map<CatalogTab, HTMLButtonElement>>(new Map());

  const focusTab = (next: CatalogTab) => {
    onChange(next);
    // Focus must move to the new tab to satisfy the roving-tabindex
    // contract — `tabIndex` only updates on the next render, so we wait a
    // microtask before calling `.focus()`.
    queueMicrotask(() => {
      tabRefs.current.get(next)?.focus();
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, current: CatalogTab) => {
    const idx = CATALOG_TABS.indexOf(current);
    let nextTab: CatalogTab | null = null;
    switch (e.key) {
      case "ArrowRight":
        nextTab = CATALOG_TABS[(idx + 1) % CATALOG_TABS.length];
        break;
      case "ArrowLeft":
        nextTab = CATALOG_TABS[(idx - 1 + CATALOG_TABS.length) % CATALOG_TABS.length];
        break;
      case "Home":
        nextTab = CATALOG_TABS[0];
        break;
      case "End":
        nextTab = CATALOG_TABS[CATALOG_TABS.length - 1];
        break;
      default:
        return;
    }
    if (nextTab !== null && nextTab !== current) {
      e.preventDefault();
      focusTab(nextTab);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={t("catalog.tabs.label")}
      className="flex flex-wrap gap-1 border-b border-border"
    >
      {CATALOG_TABS.map((tab) => {
        const selected = tab === value;
        return (
          <button
            key={tab}
            id={tabId(tab)}
            ref={(node) => {
              if (node) tabRefs.current.set(tab, node);
              else tabRefs.current.delete(tab);
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelId(tab)}
            tabIndex={selected ? 0 : -1}
            data-testid={`catalog-tab-${tab}`}
            onClick={() => onChange(tab)}
            onKeyDown={(e) => handleKeyDown(e, tab)}
            className={cn(
              "-mb-px border-b-2 px-4 py-2 font-sans text-sm transition-colors",
              selected
                ? "border-wander-orange font-bold text-foreground"
                : "border-transparent font-normal text-muted-foreground hover:text-foreground",
            )}
          >
            {t(`catalog.tabs.${tab}`)}
          </button>
        );
      })}
    </div>
  );
}
